import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "student" | "school" | "coaching_center" | "institution";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: { full_name: string | null; avatar_url: string | null } | null;
  isAdmin: boolean;
  role: AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  isAdmin: false,
  role: null,
  loading: true,
  signOut: async () => {},
});

export const ROLE_HOME: Record<AppRole, string> = {
  admin: "/admin",
  student: "/dashboard",
  school: "/institution-dashboard",
  coaching_center: "/institution-dashboard",
  institution: "/institution-dashboard",
};

export const INSTITUTION_ROLES: AppRole[] = ["school", "coaching_center", "institution"];
export const ROLE_PRIORITY: AppRole[] = ["admin", "school", "coaching_center", "institution", "student"];
export const getPrimaryRole = (roles: readonly string[]): AppRole | null =>
  ROLE_PRIORITY.find((candidate) => roles.includes(candidate)) ?? null;

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer fetching to avoid deadlocks
          setTimeout(async () => {
            const { data: profileData } = await supabase
              .from("profiles")
              .select("full_name, avatar_url")
              .eq("user_id", session.user.id)
              .single();
            setProfile(profileData);

            const { data: rolesData } = await supabase
              .from("user_roles")
              .select("role")
              .eq("user_id", session.user.id);
            const roles = (rolesData?.map((r) => r.role) ?? []) as string[];
            const primary = getPrimaryRole(roles);
            setRole(primary);
            setIsAdmin(primary === "admin");
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
          setRole(null);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, isAdmin, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

